"use client";

import { useEffect, useRef } from "react";
import { trackEvent } from "@/lib/analytics";

const MILESTONES = [25, 50, 75, 100] as const;

type ArticleScrollTrackerProps = {
  articleSlug: string;
  category?: string | null;
};

function scrollPercent(): number {
  const doc = document.documentElement;
  const total = doc.scrollHeight - window.innerHeight;
  if (total <= 0) {
    return 100;
  }
  return Math.min(100, Math.round((window.scrollY / total) * 100));
}

export function ArticleScrollTracker({
  articleSlug,
  category,
}: ArticleScrollTrackerProps): null {
  const fired = useRef<Set<number>>(new Set());

  useEffect(() => {
    const maybeCapture = (): void => {
      const pct = scrollPercent();
      for (const m of MILESTONES) {
        if (pct >= m && !fired.current.has(m)) {
          fired.current.add(m);
          trackEvent("article_scroll_depth", {
            scroll_depth_pct: m,
            article_slug: articleSlug,
            category: category ?? undefined,
            page_path: window.location.pathname,
          });
        }
      }
    };

    maybeCapture();
    window.addEventListener("scroll", maybeCapture, { passive: true });
    return () => window.removeEventListener("scroll", maybeCapture);
  }, [articleSlug, category]);

  return null;
}
